import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from "preact/hooks";
import { memo } from "preact/compat";
import { UpgradePrompt } from "./UpgradePrompt";
import { useAuth, useNavigate } from "../contexts/AppContext";
import { renderChatMarkdown } from "../lib/chatMarkdown";
import {
  TemplateSuggestion,
  TemplateSuggestionCards,
} from "./TemplateCards";
import {
  FocusBlock,
  focusIcon,
  matchesQuery,
  mentionQueryAt,
  uniqueMention,
} from "../lib/focusBlocks";

interface Attachment {
  id: number;
  filename: string;
  mime_type: string;
  size: number;
}

interface ToolCall {
  id?: string;
  name: string;
  arguments: Record<string, unknown>;
}

// Empty-state starter chips: each click sends the canned prompt as the
// user's first message, kicking off a conversation instead of leaving a
// blank sidebar.
const STARTER_PROMPTS = [
  {
    icon: "bi-link-45deg",
    label: "Add my first link",
    prompt: "Help me add my first link to this page.",
  },
  {
    icon: "bi-palette",
    label: "Give my page a style",
    prompt: "Give my page a fresh new style — pick a look that suits it.",
  },
  {
    icon: "bi-person-lines-fill",
    label: "Write a short bio",
    prompt: "Write a short, friendly bio for my page.",
  },
];

const MUTATION_FNS = [
  "update_portal",
  "add_block",
  "update_block",
  "remove_block",
  "reorder_blocks",
  "css_set",
  // Whole-template swaps (apply_template) mutate the portal styles just like
  // css_set does, so the preview needs a refresh when they land.
  "apply_template",
  // Contact-form settings are part of the rendered page, so toggling or
  // updating the form should also trigger a preview reload.
  "set_contact_form",
];

function countMutations(script: string): number {
  return MUTATION_FNS.reduce((acc, fn) => {
    return (
      acc + (script.match(new RegExp(`\\b${fn}\\s*\\(`, "g"))?.length ?? 0)
    );
  }, 0);
}

interface ToolSummary {
  text: string;
  isError: boolean;
}

interface StreamStep {
  text: string;
  isError: boolean;
}

function toolCallSummary(
  tc: ToolCall,
  result?: { content: string; isError: boolean },
): ToolSummary | null {
  if (result?.isError) {
    return { text: "Tool call failed", isError: true };
  }
  if (tc.name === "run_starlark") {
    const script = (tc.arguments as { script?: string }).script ?? "";
    const n = countMutations(script);
    return n > 0
      ? {
          text: `Made ${n} change${n === 1 ? "" : "s"} to webpage`,
          isError: false,
        }
      : null;
  }
  return null;
}

function toolCallPendingLabel(tc: ToolCall): string {
  if (tc.name === "run_starlark") {
    const script = (tc.arguments as { script?: string }).script ?? "";
    const n = countMutations(script);
    return n > 0
      ? `Applying ${n} webpage change${n === 1 ? "" : "s"}…`
      : "Checking current webpage…";
  }
  return `Running ${tc.name}…`;
}

function toolCallChangesPortal(tc: ToolCall): boolean {
  return toolCallSummary(tc) !== null;
}

interface MessageGroup {
  role: string;
  msgs: Message[];
}

function groupByRole(messages: Message[]): MessageGroup[] {
  const groups: MessageGroup[] = [];
  for (const msg of messages) {
    const last = groups[groups.length - 1];
    const lastMsg = last?.msgs[last.msgs.length - 1];
    // Break the group if either the incoming or previous message has tool_calls,
    // so each tool-calling turn gets its own bubble with its result shown inline.
    const canMerge =
      last &&
      last.role === msg.role &&
      !msg.tool_calls?.length &&
      !lastMsg?.tool_calls?.length;
    if (canMerge) {
      last.msgs.push(msg);
    } else {
      groups.push({ role: msg.role, msgs: [msg] });
    }
  }
  return groups;
}

function formatTime(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  if (isToday) return time;
  const date = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  return `${date} ${time}`;
}

function formatFinishedIn(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "";
  if (ms < 1000) return "Finished in <1s";

  const totalSeconds = Math.max(1, Math.round(ms / 1000));
  if (totalSeconds < 60) return `Finished in ${totalSeconds}s`;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) {
    return seconds === 0
      ? `Finished in ${minutes}m`
      : `Finished in ${minutes}m ${seconds}s`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes === 0
    ? `Finished in ${hours}h`
    : `Finished in ${hours}h ${remainingMinutes}m`;
}

function assistantFinishedInLabel(
  groups: MessageGroup[],
  groupIndex: number,
): string {
  const group = groups[groupIndex];
  if (!group || group.role !== "assistant") return "";

  // Consecutive assistant groups belong to the same user turn when tool calls
  // are involved. Only the last assistant bubble in that run should claim the
  // full completion time for the user's request.
  if (groups[groupIndex + 1]?.role === "assistant") return "";

  for (let i = groupIndex - 1; i >= 0; i--) {
    const candidate = groups[i];
    if (candidate.role !== "user") continue;

    const startedAt = new Date(
      candidate.msgs[candidate.msgs.length - 1].created_at,
    );
    const finishedAt = new Date(group.msgs[group.msgs.length - 1].created_at);
    if (
      Number.isNaN(startedAt.getTime()) ||
      Number.isNaN(finishedAt.getTime())
    ) {
      return "";
    }
    return formatFinishedIn(finishedAt.getTime() - startedAt.getTime());
  }

  return "";
}

interface Message {
  id: number;
  role: string;
  content: string;
  created_at: string;
  attachments?: Attachment[];
  tool_calls?: ToolCall[];
  tool_call_id?: string;
  // version_id is set on the assistant message that closes a turn that
  // mutated the portal — that version is a checkpoint we can restore to.
  version_id?: number;
  // focus_blocks records what the user was pointing at when they sent this
  // message (FT-FOCUS), with the labels those items carried at the time.
  focus_blocks?: FocusBlock[];
  // True on the oldest message still sent to the LLM after chat-history
  // trimming kicked in. Older transcript remains visible above this line.
  context_cutoff?: boolean;
  // True when this message was archived by a restore — visible for review
  // but excluded from LLM context.
  archived?: boolean;
  // Template cards the assistant suggested alongside this message. These
  // are produced by the suggest_templates Starlark builtin and rendered
  // inline as clickable cards; the server now persists them on the message
  // so they survive reloads and reattach.
  suggested_templates?: TemplateSuggestion[];
}

interface StreamEvent {
  type: string;
  text?: string;
  tool_call?: ToolCall;
  result?: string;
  is_error?: boolean;
  message?: Message;
  error?: string;
  templates?: TemplateSuggestion[];
}

/** Imperative handle so the preview can put the cursor in the composer. */
export interface ComposerHandle {
  focus: () => void;
}

export function ChatSidebar({
  slug,
  width,
  onPortalChanged,
  focus,
  onFocusChange,
  items,
  onPreviewItem,
  composerRef,
  mobile,
  onMessagesChanged,
}: {
  slug: string;
  width: number;
  onPortalChanged: () => void;
  /** FT-FOCUS: items pointed at, owned by PortalView. */
  focus: FocusBlock[];
  onFocusChange: (focus: FocusBlock[]) => void;
  /** Everything on the page that can be pointed at; feeds the @-menu. */
  items: FocusBlock[];
  /** The @-menu's active row, mirrored into the preview as a highlight. */
  onPreviewItem: (id: string | null) => void;
  composerRef?: { current: ComposerHandle | null };
  /** FT-MOBILE: when true, override width to 100% and fill the pane. */
  mobile?: boolean;
  /** FT-MOBILE: called after messages load/change so parent can set default pane. */
  onMessagesChanged?: (hasMessages: boolean) => void;
}) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [streamStatus, setStreamStatus] = useState("");
  const [streamSteps, setStreamSteps] = useState<StreamStep[]>([]);
  // Template cards emitted by suggest_templates during the current turn.
  // Captured and attached to the final assistant message when it arrives.
  const [suggestedTemplates, setSuggestedTemplates] = useState<
    TemplateSuggestion[]
  >([]);
  // Mirror state into a ref so handleStreamEvent can read the latest pending
  // suggestions synchronously, without waiting for a render commit. A useEffect
  // sync is too late: if the 'templates' and 'final_message' events land in the
  // same batch, the ref would still be empty when we attach cards to the message.
  const suggestedTemplatesRef = useRef<TemplateSuggestion[]>([]);
  suggestedTemplatesRef.current = suggestedTemplates;
  // FT-BILLING: chat handler returns 402 with {error:"limit_reached"}
  // when the user has burned their chat-turn budget. We swap the input
  // for an UpgradePrompt instead of alerting.
  const [limitReached, setLimitReached] = useState(false);
  // Show archived (reverted) messages inline.
  const [showArchived, setShowArchived] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [mentionActive, setMentionActive] = useState(0);
  // Position of an "@" the user pressed Escape on. Dismissal is remembered per
  // "@" rather than as a flag so typing a fresh one always reopens the menu.
  const [mentionDismissedAt, setMentionDismissedAt] = useState<number | null>(
    null,
  );
  // Kept in a ref so handleStreamEvent / processStream stay referentially
  // stable even if the parent passes a fresh onPortalChanged each render —
  // otherwise the reattach effect below would re-run on every parent render.
  const onPortalChangedRef = useRef(onPortalChanged);
  onPortalChangedRef.current = onPortalChanged;

  // The composer is a contentEditable, so text edits have to be written to the
  // DOM and mirrored into state, then the caret restored to the end (replacing
  // innerText collapses the selection to the start otherwise).
  const setComposerText = useCallback((next: string) => {
    setInput(next);
    const el = inputRef.current;
    if (!el) return;
    el.innerText = next;
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, []);

  useEffect(() => {
    if (!composerRef) return;
    composerRef.current = { focus: () => inputRef.current?.focus() };
    return () => {
      composerRef.current = null;
    };
  }, [composerRef]);

  // FT-FOCUS @-menu. Typing "@" filters the page's items; the active row is
  // highlighted and scrolled to in the preview, so you can SEE which one you'd
  // pick instead of matching label text in your head.
  const mention = mentionQueryAt(input);
  const alreadyPicked = new Set(focus.map((b) => b.id));
  const mentionItems = mention
    ? items
        .filter((i) => !alreadyPicked.has(i.id) && matchesQuery(i, mention.query))
        .slice(0, 8)
    : [];
  // No matches closes the menu. That's what keeps a spaces-allowed query from
  // hijacking ordinary prose ("email me @ the address below") — the pattern
  // matches for a keystroke or two, finds nothing, and gets out of the way.
  const mentionOpen =
    mention !== null &&
    mentionItems.length > 0 &&
    mention.at !== mentionDismissedAt;
  const activeIdx = mentionItems.length
    ? Math.min(mentionActive, mentionItems.length - 1)
    : 0;
  const activeMentionID = mentionOpen ? mentionItems[activeIdx]?.id : null;

  // Filtering restarts the selection; arrowing does not (it sets the index
  // directly), so keying this on the query alone is what makes both work.
  useEffect(() => {
    setMentionActive(0);
  }, [mention?.query]);

  useEffect(() => {
    onPreviewItem(activeMentionID ?? null);
  }, [activeMentionID, onPreviewItem]);

  const selectMention = useCallback(
    (item: FocusBlock) => {
      if (!mention) return;
      if (focus.some((b) => b.id === item.id)) return;
      // Replace the typed "@twi" with the canonical "@Twitter" rather than
      // deleting it. focus_blocks is an unordered set, so the words are the
      // only thing that says which pick plays which role in the sentence:
      // strip them and "remove the @facebook item, and move the @twitter item
      // to the top" becomes two anonymous chips and two contradictory verbs.
      const text = uniqueMention(
        item.label,
        focus.map((b) => b.mention).filter((m): m is string => !!m),
      );
      onFocusChange([...focus, { ...item, mention: text }]);
      setComposerText(`${input.slice(0, mention.at)}${text} `);
      setMentionDismissedAt(null);
    },
    [mention, focus, input, onFocusChange, setComposerText],
  );

  // A mention only means something while its text is in the sentence, so a
  // pick whose words the user deleted has to be dropped. Reconciled at SEND,
  // not on every keystroke: mid-edit the composer passes through states where
  // the text is transiently absent — select-all-and-retype starts from a
  // single character — and pruning there silently threw away picks the user
  // was in the middle of keeping. Send is the only moment the message and the
  // picks actually have to agree. Click-picks carry no text and always stay.
  const mentionsPresentIn = (text: string, picks: FocusBlock[]) =>
    picks.filter((b) => !b.mention || text.includes(b.mention));

  const loadMessages = useCallback(async () => {
    try {
      const res = await fetch(
        `/api/portals/${encodeURIComponent(slug)}/messages`,
      );
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
    } catch {
      setMessages([]);
    }
  }, [slug]);

  // FT-MOBILE: tell parent whether messages exist (for default pane choice).
  useEffect(() => {
    onMessagesChanged?.(messages.length > 0);
  }, [messages.length, onMessagesChanged]);

  // Clicking a suggested template applies it directly. This used to send
  // "Use the X template" as a chat message, which spent a metered chat turn
  // and ~30s of LLM time on a decision the click had already made — and left
  // the agent free to do something else entirely. The endpoint applies the
  // named template and records the exchange in the transcript itself.
  //
  // Deliberately not gated on `loading`: cards only exist once the turn has
  // called suggest_templates, and the server refuses every tool call after
  // that, so an in-flight turn can no longer touch the page. Gating here would
  // just make the cards dead for the few seconds the closing line streams.
  const applyTemplate = useCallback(
    async (name: string) => {
      try {
        const res = await fetch(
          `/api/portals/${encodeURIComponent(slug)}/apply-template`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name }),
          },
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          alert(data.error || "Could not apply that template");
          return;
        }
        setSuggestedTemplates([]);
        onPortalChangedRef.current();
        await loadMessages();
      } catch {
        alert("Could not apply that template");
      }
    },
    [slug, loadMessages],
  );

  // Translates one NDJSON stream event into UI state. Shared by the live
  // send path and the reattach path, so a refreshed client rebuilds the
  // same "thinking…" / tool-step view from a replayed backlog. Throws on an
  // "error" event so the caller's catch surfaces it.
  const handleStreamEvent = useCallback((event: StreamEvent) => {
    switch (event.type) {
      case "status":
        setStreamStatus(event.text || "Thinking…");
        break;
      case "tool_call":
        if (event.tool_call) {
          setStreamSteps((prev) => [
            ...prev,
            {
              text: toolCallPendingLabel(event.tool_call as ToolCall),
              isError: false,
            },
          ]);
        }
        break;
      case "tool_result":
        if (!event.tool_call) break;
        {
          const toolCall = event.tool_call as ToolCall;
          const isError = event.is_error === true;
          const summary = toolCallSummary(toolCall, {
            content: event.result || "",
            isError,
          });
          setStreamSteps((prev) => [
            ...prev,
            {
              text:
                summary?.text ||
                (isError ? "Tool call failed" : "Checked current webpage"),
              isError: summary?.isError ?? isError,
            },
          ]);
          if (!isError && toolCallChangesPortal(toolCall)) {
            onPortalChangedRef.current();
          }
        }
        break;
      case "templates":
        if (event.templates && event.templates.length > 0) {
          // Update the ref synchronously so a same-batch final_message can
          // read the accumulated suggestions without waiting for a render.
          const next = [...suggestedTemplatesRef.current, ...event.templates!];
          suggestedTemplatesRef.current = next;
          setSuggestedTemplates(next);
        }
        break;
      case "final_message":
        if (event.message) {
          const msg = event.message as Message;
          // Attach any template suggestions emitted during this turn to the
          // assistant message so they render inline and survive the stream.
          // Prefer the server-provided suggestions (they persist across reloads)
          // and fall back to the ref for any in-flight templates that arrived
          // before the final message.
          const existing = msg.suggested_templates ?? [];
          const pending = suggestedTemplatesRef.current;
          const templates = existing.length > 0 ? existing : pending;
          const withTemplates = templates.length > 0
            ? { ...msg, suggested_templates: templates }
            : msg;
          // Dedupe by id: on reattach the persisted message may already be
          // present from loadMessages, and final_message carries the same id.
          setMessages((prev) =>
            prev.some((m) => m.id === msg.id) ? prev : [...prev, withTemplates],
          );
          setSuggestedTemplates([]);
          suggestedTemplatesRef.current = [];
        }
        break;
      case "error":
        setSuggestedTemplates([]);
        suggestedTemplatesRef.current = [];
        throw new Error(event.error || "Chat failed");
    }
  }, []);

  // Drains an NDJSON chat stream to completion, dispatching each event.
  const processStream = useCallback(
    async (res: Response) => {
      if (!res.body) throw new Error("Streaming unavailable");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
        let newline = buffer.indexOf("\n");
        while (newline >= 0) {
          const line = buffer.slice(0, newline).trim();
          buffer = buffer.slice(newline + 1);
          if (line) handleStreamEvent(JSON.parse(line) as StreamEvent);
          newline = buffer.indexOf("\n");
        }
        if (done) break;
      }
      if (buffer.trim()) handleStreamEvent(JSON.parse(buffer) as StreamEvent);
    },
    [handleStreamEvent],
  );

  // On mount (and slug change): load history, then check whether a chat turn
  // is still running detached on the server (the user sent a message and
  // refreshed before it finished). If so, reattach and follow it to the end
  // instead of leaving them staring at an answer-less message.
  useEffect(() => {
    let cancelled = false;
    setStreamStatus("");
    setStreamSteps([]);
    (async () => {
      await loadMessages();
      if (cancelled) return;
      try {
        const res = await fetch(
          `/api/portals/${encodeURIComponent(slug)}/chat/stream`,
        );
        if (cancelled || res.status === 204 || !res.ok || !res.body) return;
        setLoading(true);
        setStreamStatus("Thinking…");
        setStreamSteps([]);
        await processStream(res);
      } catch {
        if (!cancelled) await loadMessages();
      } finally {
        if (!cancelled) {
          setLoading(false);
          setStreamStatus("");
          setStreamSteps([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, loadMessages, processStream]);

  useLayoutEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, streamStatus, streamSteps]);

  const uploadFiles = async (files: FileList) => {
    setUploading(true);
    for (const file of Array.from(files)) {
      if (!file.type.startsWith("image/")) {
        alert(`"${file.name}" is not an image`);
        continue;
      }
      if (file.size > 1 * 1024 * 1024) {
        alert(`"${file.name}" is too large (max 1MB)`);
        continue;
      }

      const formData = new FormData();
      formData.append("file", file);

      try {
        const res = await fetch(
          `/api/portals/${encodeURIComponent(slug)}/uploads`,
          {
            method: "POST",
            body: formData,
          },
        );
        const data = await res.json();
        if (data.error) {
          alert(data.error);
        } else {
          setAttachments((prev) => [...prev, data]);
        }
      } catch {
        alert(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
  };

  const handleFileSelect = async (e: Event) => {
    const target = e.currentTarget as HTMLInputElement;
    const files = target.files;
    if (!files || files.length === 0) return;
    await uploadFiles(files);
    target.value = "";
  };

  const removeAttachment = (id: number) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (dragCounterRef.current === 1) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragging(false);
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      void uploadFiles(files);
    }
  };

  // textOverride lets the empty-state prompt chips send a canned first
  // message without routing it through the contentEditable input.
  const sendMessage = async (textOverride?: string) => {
    const userContent = (textOverride ?? input).trim();
    if ((!userContent && attachments.length === 0) || loading) return;
    setInput("");
    if (inputRef.current) {
      inputRef.current.innerText = "";
    }
    const currentAttachments = [...attachments];
    setAttachments([]);
    // The picks are consumed by this message, like attachments — they describe
    // what THIS request is about, not a standing selection. Mentions whose text
    // is no longer in the message go with it: the binding the model relies on
    // is the words, so a pick the sentence no longer names is worse than none.
    const currentFocus = mentionsPresentIn(userContent, focus);
    onFocusChange([]);
    setMentionDismissedAt(null);
    setLoading(true);
    setShowArchived(false);
    setLimitReached(false);
    setStreamStatus("Thinking…");
    setStreamSteps([]);
    setSuggestedTemplates([]);
    suggestedTemplatesRef.current = [];

    const optimisticUserMsg: Message = {
      id: -Date.now(),
      role: "user",
      content: userContent,
      created_at: new Date().toISOString(),
      attachments: currentAttachments,
      focus_blocks: currentFocus,
    };
    setMessages((prev) => [...prev, optimisticUserMsg]);

    try {
      const res = await fetch(
        `/api/portals/${encodeURIComponent(slug)}/chat/stream`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            content: userContent,
            upload_ids: currentAttachments.map((a) => a.id),
            focus_blocks: currentFocus,
          }),
        },
      );

      if (res.status === 402) {
        const data = await res.json().catch(() => ({}));
        if (data.error === "limit_reached") {
          // Drop the optimistic user message — it never landed server-side.
          setMessages((prev) =>
            prev.filter((m) => m.id !== optimisticUserMsg.id),
          );
          // Nothing landed server-side, so hand the picks back rather than
          // making the user re-click them after upgrading.
          onFocusChange(currentFocus);
          setLimitReached(true);
          return;
        }
      }
      if (res.status === 409) {
        // A turn is already running for this portal (e.g. another tab). Drop
        // the optimistic message and reattach to the in-flight turn instead.
        setMessages((prev) =>
          prev.filter((m) => m.id !== optimisticUserMsg.id),
        );
        onFocusChange(currentFocus);
        await loadMessages();
        const reattach = await fetch(
          `/api/portals/${encodeURIComponent(slug)}/chat/stream`,
        );
        if (reattach.ok && reattach.body) {
          await processStream(reattach);
        }
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to send message");
      }

      await processStream(res);
    } catch (err) {
      await loadMessages();
      alert(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      setLoading(false);
      setStreamStatus("");
      setStreamSteps([]);
      setSuggestedTemplates([]);
      suggestedTemplatesRef.current = [];
    }
  };

  const toolResults = useMemo(() => {
    const map = new Map<string, { content: string; isError: boolean }>();
    for (const msg of messages) {
      if (msg.role === "tool" && msg.tool_call_id) {
        const isError =
          msg.content.startsWith("Error:") ||
          msg.content.startsWith("Traceback");
        map.set(msg.tool_call_id, { content: msg.content, isError });
      }
    }
    return map;
  }, [messages]);

  const messageGroups = useMemo(
    () => groupByRole(messages.filter((msg) => msg.role !== "tool")),
    [messages],
  );

  // Identify the most recent assistant turn that produced a checkpoint —
  // older "Restore" buttons read as the destination, the latest one is
  // current state and gets a "Current" pill instead of a button.
  const latestVersionedTurn = useMemo(() => {
    const assistantTurnsWithVersion = messageGroups.filter((g) => {
      if (g.role !== "assistant") return false;
      return g.msgs.some((m) => m.version_id);
    });
    return assistantTurnsWithVersion[assistantTurnsWithVersion.length - 1];
  }, [messageGroups]);

  // Count archived messages and find where the archived tail starts.
  const archivedInfo = useMemo(() => {
    let count = 0;
    let firstIdx = -1;
    for (let i = 0; i < messageGroups.length; i++) {
      if (messageGroups[i].msgs.some((m) => m.archived)) {
        if (firstIdx === -1) firstIdx = i;
        count += messageGroups[i].msgs.length;
      }
    }
    return { count, firstIdx };
  }, [messageGroups]);

  const restore = useCallback(
    async (versionId: number) => {
      if (
        !confirm(
          "Restore the portal to this version? Newer changes stay in history and can be restored back.",
        )
      )
        return;
      try {
        const res = await fetch(
          `/api/portals/${encodeURIComponent(slug)}/revert`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ version_id: versionId }),
          },
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          alert(data.error || "Restore failed");
          return;
        }
        onPortalChanged();
        await loadMessages();
      } catch {
        alert("Restore failed");
      }
    },
    [slug, onPortalChanged, loadMessages],
  );

  return (
    <div
      class="d-flex flex-column h-100 border-end bg-body"
      style={{
        width: mobile ? "100%" : `${width}px`,
        minWidth: mobile ? "0" : "240px",
        position: "relative",
      }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 1050,
            backgroundColor: "rgba(13, 110, 253, 0.08)",
            border: "2px dashed #0d6efd",
            borderRadius: "0.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(1px)",
          }}
        >
          <div class="text-center text-primary">
            <i class="bi bi-image fs-1 d-block mb-2"></i>
            <strong>Drop images here</strong>
          </div>
        </div>
      )}
      <div class="flex-grow-1 overflow-auto p-3">
        {messages.length === 0 && (
          <div class="text-center py-5 px-2">
            <i class="bi bi-stars fs-1 mb-2 d-block text-body-secondary"></i>
            <p class="fw-semibold mb-1">Let's build your page</p>
            <p class="small text-body-secondary mb-3">
              Tell the assistant what you want — it edits the preview on the
              right as you chat.
            </p>
            <div class="d-flex flex-column gap-2 align-items-stretch">
              {STARTER_PROMPTS.map((p) => (
                <button
                  key={p.prompt}
                  type="button"
                  class="btn btn-outline-secondary btn-sm text-start"
                  disabled={loading}
                  onClick={() => sendMessage(p.prompt)}
                >
                  <i class={`bi ${p.icon} me-2`}></i>
                  {p.label}
                </button>
              ))}
            </div>
            {/* FT-FOCUS is only discoverable by hovering the preview or by
                typing "@", so say it once here rather than adding permanent
                chrome. */}
            <p class="small text-body-secondary mt-4 mb-0">
              <i class="bi bi-bullseye me-1"></i>Tip: click anything in the
              preview — or type <code>@</code> — to point the assistant at it.
            </p>
          </div>
        )}
        {messageGroups.map((group, i) => {
          const isArchived = group.msgs.some((m) => m.archived);
          const isFirstArchived = i === archivedInfo.firstIdx;

          // Collapsed: skip all archived groups, show a single divider
          // button at the first one.
          if (isArchived && !showArchived) {
            if (isFirstArchived) {
              return (
                <div key="archived-divider" class="text-center my-3">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    onClick={() => setShowArchived(true)}
                  >
                    <i class="bi bi-chevron-down me-1"></i>
                    {archivedInfo.count} reverted message
                    {archivedInfo.count !== 1 ? "s" : ""}
                  </button>
                </div>
              );
            }
            return null;
          }

          // Expanded: render all archived groups with muted styling, plus
          // a thin divider at the first one.
          return (
            <div key={`${i}-${group.msgs[0].id}`}>
              {group.msgs.some((m) => m.context_cutoff) && (
                <ContextCutoffLine />
              )}
              {isFirstArchived && (
                <div class="text-center small text-body-secondary border-top pt-2 mb-2">
                  <i class="bi bi-arrow-counterclockwise me-1"></i>
                  Reverted — not sent to the AI
                </div>
              )}
              <div
                class={isArchived ? "archived-message-group opacity-75" : ""}
                style={
                  isArchived
                    ? { filter: "grayscale(0.3)", fontSize: "0.92em" }
                    : undefined
                }
              >
                <MessageGroupItem
                  group={group}
                  toolResults={toolResults}
                  latestVersionedTurn={
                    isArchived ? undefined : latestVersionedTurn
                  }
                  turnDurationLabel={assistantFinishedInLabel(
                    messageGroups,
                    i,
                  )}
                  onRestore={restore}
                  onApplyTemplate={applyTemplate}
                  loading={loading}
                />
              </div>
            </div>
          );
        })}
        {loading && (
          <StreamingStatusBubble
            status={streamStatus}
            steps={streamSteps}
            templates={suggestedTemplates}
            onApplyTemplate={applyTemplate}
          />
        )}
        <div ref={bottomRef}></div>
      </div>
      <div class={`p-3 border-top${mobile ? " editor-composer-wrap" : ""}`}>
        {attachments.length > 0 && (
          <div class="d-flex gap-2 mb-2 flex-wrap">
            {attachments.map((att) => (
              <div key={att.id} class="position-relative">
                <img
                  src={`/api/uploads/${att.id}`}
                  alt={att.filename}
                  class="rounded border"
                  style={{
                    height: "60px",
                    width: "60px",
                    objectFit: "cover",
                  }}
                />
                <button
                  type="button"
                  class="btn btn-sm btn-danger position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center"
                  style={{
                    width: "16px",
                    height: "16px",
                    fontSize: "10px",
                    borderRadius: "50%",
                  }}
                  onClick={() => removeAttachment(att.id)}
                >
                  <i class="bi bi-x" style={{ lineHeight: 1 }}></i>
                </button>
              </div>
            ))}
          </div>
        )}
        {focus.length > 0 && (
          <div class="d-flex gap-1 mb-2 flex-wrap align-items-center">
            <span class="small text-body-secondary">
              <i class="bi bi-bullseye me-1"></i>Editing:
            </span>
            {focus.map((b) => (
              <FocusChip
                key={b.id}
                block={b}
                pop
                onRemove={() =>
                  onFocusChange(focus.filter((x) => x.id !== b.id))
                }
              />
            ))}
          </div>
        )}
        {limitReached && (
          <UpgradePrompt reason="You've used your chat-turn budget for this period." />
        )}
        <div
          class="position-relative"
          style={limitReached ? "display:none" : undefined}
        >
          {mentionOpen && (
            <div
              class="dropdown-menu show w-100 p-1 shadow"
              style={{
                position: "absolute",
                bottom: "100%",
                left: 0,
                marginBottom: "4px",
                maxHeight: "40vh",
                overflowY: "auto",
                zIndex: 1040,
              }}
            >
              {mentionItems.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  class={`dropdown-item d-flex align-items-center gap-2 rounded text-truncate${
                    i === activeIdx ? " active" : ""
                  }`}
                  // Hovering previews too: the highlight in the preview should
                  // follow the pointer as readily as the keyboard.
                  onMouseEnter={() => setMentionActive(i)}
                  onMouseDown={(e: Event) => e.preventDefault()}
                  onClick={() => selectMention(item)}
                >
                  <i class={`bi ${focusIcon(item.kind)}`}></i>
                  <span class="text-truncate">{item.label}</span>
                  <span class="small opacity-75 ms-auto">{item.kind}</span>
                </button>
              ))}
            </div>
          )}
          <div
            ref={inputRef}
            contentEditable
            role="textbox"
            aria-multiline="true"
            class={`form-control${mobile ? " mobile-composer" : ""}`}
            style={{
              minHeight: "4.5em",
              maxHeight: "30vh",
              overflowY: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
            placeholder="Type a message..."
            onInput={(e) =>
              setInput((e.currentTarget as HTMLDivElement).innerText)
            }
            onPaste={(e: ClipboardEvent) => {
              e.preventDefault();
              const text = e.clipboardData?.getData("text/plain") || "";
              document.execCommand("insertText", false, text);
            }}
            onKeyDown={(e: KeyboardEvent) => {
              // The @-menu owns these keys while it's open — Enter especially,
              // which must pick the highlighted item rather than send.
              if (mentionOpen) {
                const n = mentionItems.length;
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setMentionActive((i) => (Math.min(i, n - 1) + 1) % n);
                  return;
                }
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setMentionActive((i) => (Math.min(i, n - 1) + n - 1) % n);
                  return;
                }
                if (e.key === "Enter" || e.key === "Tab") {
                  e.preventDefault();
                  selectMention(mentionItems[activeIdx]);
                  return;
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setMentionDismissedAt(mention?.at ?? null);
                  return;
                }
              }
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
          />
          <div class="d-flex gap-2 mt-2 justify-content-end">
            <button
              type="button"
              class="btn btn-outline-secondary"
              disabled={loading || uploading}
              onClick={() => fileInputRef.current?.click()}
              title="Attach image"
            >
              <i class="bi bi-paperclip"></i>
            </button>
            <button
              type="button"
              class="btn btn-primary"
              disabled={
                loading ||
                uploading ||
                (!input.trim() && attachments.length === 0)
              }
              onClick={() => sendMessage()}
            >
              <i class="bi bi-send"></i>
            </button>
          </div>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          class="d-none"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
        />
      </div>
    </div>
  );
}

// One pointed-at item. Rendered in the composer with a remove button, and
// read-only inside a sent message so history shows what each request was
// aimed at.
function FocusChip({
  block,
  onRemove,
  pop,
}: {
  block: FocusBlock;
  onRemove?: () => void;
  /**
   * Play the attention pop on mount. Composer chips only: the chip appears in
   * the sidebar while the user is looking at the preview, so it needs to
   * announce itself. Keyed by block id, so each newly added chip mounts and
   * animates once while the existing ones sit still. History chips never pop.
   */
  pop?: boolean;
}) {
  return (
    <span
      class={`badge text-bg-secondary fw-normal d-inline-flex align-items-center gap-1${
        pop ? " focus-chip-pop" : ""
      }`}
    >
      <i class={`bi ${focusIcon(block.kind)}`}></i>
      <span class="text-truncate" style={{ maxWidth: "11rem" }}>
        {block.label}
      </span>
      {onRemove && (
        <button
          type="button"
          class="btn-close btn-close-white p-0"
          style={{ fontSize: "0.5rem" }}
          aria-label={`Stop editing ${block.label}`}
          onClick={onRemove}
        ></button>
      )}
    </span>
  );
}

function ContextCutoffLine() {
  return (
    <div class="my-3 d-flex align-items-center gap-2 small text-body-secondary">
      <div class="flex-grow-1 border-top"></div>
      <span>
        Older messages above this line are still shown here, but are no longer
        sent to the AI.
      </span>
      <div class="flex-grow-1 border-top"></div>
    </div>
  );
}

function StreamingStatusBubble({
  status,
  steps,
  templates,
  onApplyTemplate,
}: {
  status: string;
  steps: StreamStep[];
  templates?: TemplateSuggestion[];
  onApplyTemplate?: (name: string) => void;
}) {
  return (
    <div class="mb-3">
      <div
        class="d-inline-block p-2 rounded bg-body-tertiary border"
        style={{ maxWidth: "92%", textAlign: "left" }}
      >
        <div class="small fw-bold mb-1">Assistant</div>
        <div class="d-flex align-items-center gap-2">
          <div
            class="spinner-border spinner-border-sm text-body-secondary"
            role="status"
          >
            <span class="visually-hidden">Thinking...</span>
          </div>
          <div>{status || "Thinking…"}</div>
        </div>
        {steps.length > 0 && (
          <div class="small mt-2 d-flex flex-column gap-1">
            {steps.map((step, i) => (
              <div
                key={i}
                class={step.isError ? "text-danger" : "text-body-secondary"}
              >
                <i
                  class={`bi ${step.isError ? "bi-exclamation-circle" : "bi-check2-circle"} me-1`}
                ></i>
                {step.text}
              </div>
            ))}
          </div>
        )}
        {templates && templates.length > 0 && onApplyTemplate && (
          <TemplateSuggestionCards
            templates={templates}
            onApply={onApplyTemplate}
          />
        )}
      </div>
    </div>
  );
}

const MessageGroupItem = memo(function MessageGroupItem({
  group,
  toolResults,
  latestVersionedTurn,
  turnDurationLabel,
  onRestore,
  onApplyTemplate,
  loading,
}: {
  group: MessageGroup;
  toolResults: Map<string, { content: string; isError: boolean }>;
  latestVersionedTurn?: MessageGroup;
  turnDurationLabel?: string;
  onRestore: (versionId: number) => void;
  onApplyTemplate?: (name: string) => void;
  loading?: boolean;
}) {
  const isUser = group.role === "user";
  const content = group.msgs
    .map((m) => m.content)
    .filter(Boolean)
    .join("\n\n");
  const allToolCalls = group.msgs.flatMap((m) => m.tool_calls ?? []);
  const toolSummaries = allToolCalls
    .map((tc) =>
      toolCallSummary(tc, tc.id ? toolResults.get(tc.id) : undefined),
    )
    .filter((s): s is ToolSummary => s !== null);
  const allAttachments = group.msgs.flatMap((m) => m.attachments ?? []);
  const allFocus = group.msgs.flatMap((m) => m.focus_blocks ?? []);
  const allSuggestedTemplates = group.msgs.flatMap(
    (m) => m.suggested_templates ?? [],
  );
  const lastMsg = group.msgs[group.msgs.length - 1];
  const versionId = !isUser
    ? group.msgs.map((m) => m.version_id).find(Boolean)
    : undefined;
  const isCurrent = versionId !== undefined && group === latestVersionedTurn;
  const timestampLabel = formatTime(lastMsg.created_at);

  if (!isUser && !content && toolSummaries.length === 0) return null;

  return (
    <div class={`mb-3 ${isUser ? "text-end" : ""}`}>
      <div
        class={`d-inline-block p-2 rounded ${isUser ? "bg-primary-subtle text-primary-emphasis" : "bg-body-tertiary border"}`}
        style={{ maxWidth: "92%", textAlign: "left" }}
      >
        <div class="small fw-bold mb-1">{isUser ? "You" : "Assistant"}</div>
        {allFocus.length > 0 && (
          <div class="d-flex gap-1 mb-1 flex-wrap">
            {allFocus.map((b) => (
              <FocusChip key={b.id} block={b} />
            ))}
          </div>
        )}
        {content && (
          <div
            class="markdown-content"
            dangerouslySetInnerHTML={{
              __html: renderChatMarkdown(content),
            }}
          />
        )}
        {toolSummaries.map((summary, j) => (
          <div
            key={j}
            class={`small fst-italic mt-1 ${summary.isError ? "text-danger" : "text-body-secondary"}`}
          >
            <i
              class={`bi ${summary.isError ? "bi-exclamation-circle" : "bi-pencil-square"} me-1`}
            ></i>
            {summary.text}
          </div>
        ))}
        {allSuggestedTemplates.length > 0 && onApplyTemplate && (
          <TemplateSuggestionCards
            templates={allSuggestedTemplates}
            onApply={onApplyTemplate}
          />
        )}
        {versionId !== undefined &&
          (isCurrent ? (
            <div class="small text-body-secondary fst-italic mt-1">
              <i class="bi bi-bookmark-check me-1"></i>Current state
            </div>
          ) : (
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary mt-1 py-0 px-2"
              style={{ fontSize: "0.75rem" }}
              disabled={loading}
              onClick={() => onRestore(versionId)}
              title="Restore the portal to this checkpoint"
            >
              <i class="bi bi-arrow-counterclockwise me-1"></i>Restore this
              version
            </button>
          ))}
        {allAttachments.length > 0 && (
          <div class="d-flex gap-2 mt-2 flex-wrap">
            {allAttachments.map((att) => (
              <a
                key={att.id}
                href={`/api/uploads/${att.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={`/api/uploads/${att.id}`}
                  alt={att.filename}
                  class="rounded border"
                  style={{
                    maxHeight: "120px",
                    maxWidth: "200px",
                    objectFit: "cover",
                  }}
                />
              </a>
            ))}
          </div>
        )}
        {(timestampLabel || turnDurationLabel) && (
          <div
            class="small mt-1 text-body-secondary"
            style={{ fontSize: "0.7rem" }}
          >
            {turnDurationLabel ? (
              <div class="d-flex align-items-center gap-2">
                <span>{turnDurationLabel}</span>
                {timestampLabel && (
                  <span class="ms-auto">{timestampLabel}</span>
                )}
              </div>
            ) : (
              <div class="text-end">{timestampLabel}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export function UserMenuButton() {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const email = auth.status === "authed" ? auth.email : "";
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  return (
    <div ref={containerRef} class="position-relative">
      <button
        type="button"
        class={`btn btn-outline-secondary btn-sm${open ? " active" : ""}`}
        onClick={() => setOpen((v) => !v)}
        title={email}
      >
        <i class="bi bi-person-circle"></i>
      </button>
      {open && (
        <div
          class="dropdown-menu show"
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: "4px",
            zIndex: 1000,
            minWidth: "160px",
          }}
        >
          <div class="dropdown-item-text text-body-secondary small px-3 py-1">
            {email}
          </div>
          <div class="dropdown-divider"></div>
          <button
            type="button"
            class="dropdown-item"
            onClick={() => {
              setOpen(false);
              navigate("/account");
            }}
          >
            <i class="bi bi-person-circle me-2"></i>Account
          </button>
          <button
            type="button"
            class="dropdown-item"
            onClick={() => {
              setOpen(false);
              logout();
            }}
          >
            <i class="bi bi-box-arrow-right me-2"></i>Log out
          </button>
        </div>
      )}
    </div>
  );
}
